import {
  Badge,
  Button,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@devhop/ui";
import {
  CalendarIcon,
  ChevronDownIcon,
  ExternalLinkIcon,
  LayoutGridIcon,
  ListIcon,
  MapPinIcon,
  PencilIcon,
  PlusIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatTime, getTodayYYYYMMDD } from "@/shared/lib/date-time";
import { formatDateLocal } from "@/shared/lib/date-time-performance";
import { performanceApi } from "../api/client";
import { PerformanceBrandPrintMenu } from "../ui/PerformanceBrandPrintMenu";
import type {
  RehearsalSessionDTO,
  UpdateRehearsalSessionDTO,
} from "../../domain/contracts";
import type { RehearsalSessionStatusMeta } from "../../domain/services/rehearsal-sessions-ui";
import {
  rehearsalMatrixCreateSessionInput,
  type RehearsalMatrixBulkAddPayload,
} from "../../domain/services/rehearsal-schedule-matrix";
import {
  groupSessionsByDate,
  REHEARSAL_SESSION_STATUS,
  summarizeRehearsalDayStatus,
} from "../../domain/services/rehearsal-sessions-ui";
import {
  type CreateRehearsalSessionsBulkPayload,
  useBulkRescheduleRehearsalSessions,
  useBulkUpdateRehearsalSessionStatus,
  useCreateRehearsalSession,
  useCreateRehearsalSessionsBulk,
  useDeleteRehearsalSession,
  useRehearsalSessionsQuery,
  useUpdateRehearsalSession,
} from "../api/queries";
import {
  type EventPerformerOption,
  RehearsalSessionDialog,
} from "./RehearsalSessionDialog";
import { RehearsalDayRescheduleDialog } from "./RehearsalDayRescheduleDialog";
import { RehearsalScheduleMatrix } from "./RehearsalScheduleMatrix";

type RehearsalScheduleView = "matrix" | "day";
const REHEARSAL_SCHEDULE_VIEW_KEY = "fanglao.rehearsal-schedule-view";

type EventRehearsalScheduleProps = {
  eventId: string;
  eventDate: Date | string | null;
  eventPerformers: EventPerformerOption[];
  defaultRoomId?: string | null;
  defaultRoomName?: string | null;
  canManage: boolean;
  canPrint?: boolean;
  canBookStudio?: boolean;
  /** ເມື່ອໃຊ້ໃນ EventRehearsalPage — ຫົວຂໍ້ຢູ່ຫນ້າແລ້ວ */
  hideSectionHeader?: boolean;
};

function eventDateLabel(eventDate: Date | string | null) {
  if (!eventDate) return null;
  return formatDateLocal(eventDate);
}

export function EventRehearsalSchedule({
  eventId,
  eventDate,
  eventPerformers,
  defaultRoomId,
  defaultRoomName,
  canManage,
  canPrint = true,
  canBookStudio = false,
  hideSectionHeader = false,
}: EventRehearsalScheduleProps) {
  const sessionsQuery = useRehearsalSessionsQuery(eventId);
  const createBulk = useCreateRehearsalSessionsBulk(eventId);
  const createSession = useCreateRehearsalSession(eventId);
  const bulkReschedule = useBulkRescheduleRehearsalSessions(eventId);
  const bulkUpdateStatus = useBulkUpdateRehearsalSessionStatus(eventId);
  const updateSession = useUpdateRehearsalSession(eventId);
  const deleteSession = useDeleteRehearsalSession(eventId);

  const [dialog, setDialog] = useState<
    | { open: false }
    | { open: true; mode: "create"; defaultDate?: string }
    | { open: true; mode: "edit"; session: RehearsalSessionDTO }
  >({ open: false });
  const [rescheduleDay, setRescheduleDay] = useState<{
    scheduledDate: string;
    performerCount: number;
    studioSession?: {
      roomBookingId?: string | null;
      roomId?: string | null;
      roomName?: string | null;
      bookingStartTime?: string | null;
      bookingEndTime?: string | null;
    } | null;
  } | null>(null);

  const sessions = sessionsQuery.data ?? [];
  const dateGroups = useMemo(() => groupSessionsByDate(sessions), [sessions]);
  const workDayLabel = eventDateLabel(eventDate);
  const today = useMemo(() => getTodayYYYYMMDD(), []);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const hasInitializedExpandedDate = useRef(false);
  const [scheduleView, setScheduleView] = useState<RehearsalScheduleView>(() => {
    if (typeof window === "undefined") return "matrix";
    const stored = window.sessionStorage.getItem(REHEARSAL_SCHEDULE_VIEW_KEY);
    return stored === "day" ? "day" : "matrix";
  });

  useEffect(() => {
    window.sessionStorage.setItem(REHEARSAL_SCHEDULE_VIEW_KEY, scheduleView);
  }, [scheduleView]);

  useEffect(() => {
    if (hasInitializedExpandedDate.current || dateGroups.length === 0) return;
    hasInitializedExpandedDate.current = true;
    const hasToday = dateGroups.some((g) => g.scheduledDate === today);
    setExpandedDate(hasToday ? today : null);
  }, [dateGroups, today]);

  const submitting =
    createBulk.isPending ||
    createSession.isPending ||
    bulkReschedule.isPending ||
    bulkUpdateStatus.isPending ||
    updateSession.isPending ||
    deleteSession.isPending;

  const openCreate = (defaultDate?: string) => {
    setDialog({ open: true, mode: "create", defaultDate });
  };

  const handleStatusChange = async (
    session: RehearsalSessionDTO,
    status: RehearsalSessionDTO["status"],
  ) => {
    if (status === session.status) return;
    await updateSession.mutateAsync({
      sessionId: session.id,
      input: { status },
    });
  };

  const handleAddToPlan = async (
    performerId: string,
    scheduledDate: string,
  ) => {
    await createSession.mutateAsync(
      rehearsalMatrixCreateSessionInput(performerId, scheduledDate),
    );
  };

  const handleBulkStatusChange = async (
    sessionIds: string[],
    status: RehearsalSessionDTO["status"],
  ) => {
    await bulkUpdateStatus.mutateAsync({ sessionIds, status });
  };

  const handleBulkAddToPlan = async (payload: RehearsalMatrixBulkAddPayload) => {
    await createBulk.mutateAsync({
      performerIds: payload.performerIds,
      scheduledDates: payload.scheduledDates,
      location: null,
      notes: null,
      status: "planned",
    });
  };

  return (
    <div className="rounded-xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        {hideSectionHeader ? (
          <p className="text-muted-foreground text-sm">
            ວາງແຜນ ແລະ ຢືນຢັນຫຼັງຊ້ອມ — ສະຖານະ «ສຳເລັດ» ເທົ່ານັ້ນທີ່ນັບ payout
          </p>
        ) : (
          <div>
            <div className="flex items-center gap-2">
              <CalendarIcon className="size-4 text-primary" />
              <h3 className="font-semibold text-sm uppercase tracking-widest">
                ລາຍການວັນຊ້ອມ
              </h3>
            </div>
            {workDayLabel && (
              <p className="mt-1 text-muted-foreground text-xs">
                ວັນຖ່າຍງານ: {workDayLabel} — ສະຖານະ «ສຳເລັດ» ເທົ່ານັ້ນທີ່ນັບຄ່າຕົວ
              </p>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Tabs
            value={scheduleView}
            onValueChange={(value) =>
              setScheduleView(value as RehearsalScheduleView)
            }
          >
            <TabsList className="h-8">
              <TabsTrigger value="matrix" className="gap-1.5 px-2.5 text-xs">
                <LayoutGridIcon className="size-3.5" />
                ຕາຕະລາງ
              </TabsTrigger>
              <TabsTrigger value="day" className="gap-1.5 px-2.5 text-xs">
                <ListIcon className="size-3.5" />
                ຕາມວັນ
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {canPrint && sessions.length > 0 && (
            <PerformanceBrandPrintMenu
              label={scheduleView === "matrix" ? "ພິມຕາຕະລາງ" : "ພິມ PDF"}
              buildPrintUrl={(lang) =>
                performanceApi.events.rehearsalSessions.printUrl(eventId, lang, {
                  layout: scheduleView === "matrix" ? "matrix" : "day",
                })
              }
            />
          )}
          {canManage && (
            <Button
              size="sm"
              onClick={() => openCreate()}
              disabled={eventPerformers.length === 0}
            >
              <PlusIcon className="size-3.5" />
              ເພີ່ມວັນຊ້ອມ
            </Button>
          )}
        </div>
      </div>

      {sessionsQuery.isLoading ? (
        <p className="px-5 py-8 text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
      ) : sessions.length === 0 ? (
        <p className="px-5 py-8 text-center text-muted-foreground text-sm">
          {canManage
            ? eventPerformers.length === 0
              ? "ເພີ່ມນັກສະແດງໃນທີມກ່ອນ ແລ້ວກົດເພີ່ມວັນຊ້ອມ"
              : "ຍັງບໍ່ມີວັນຊ້ອມ — ກົດເພີ່ມວັນຊ້ອມ ເພື່ອວາງແຜນຫຼາຍຄົນພ້ອມກັນ"
            : "ຍັງບໍ່ມີວັນຊ້ອມ"}
        </p>
      ) : scheduleView === "matrix" ? (
        <RehearsalScheduleMatrix
          eventPerformers={eventPerformers}
          sessions={sessions}
          canManage={canManage}
          submitting={submitting}
          onAddToPlan={canManage ? handleAddToPlan : undefined}
          onBulkStatusChange={canManage ? handleBulkStatusChange : undefined}
          onBulkAddToPlan={canManage ? handleBulkAddToPlan : undefined}
        />
      ) : (
        <div className="space-y-2 p-4">
          {dateGroups.map((group) => (
            <RehearsalDayBlock
              key={group.scheduledDate}
              group={group}
              isToday={group.scheduledDate === today}
              isOpen={expandedDate === group.scheduledDate}
              onOpenChange={(open) => {
                setExpandedDate(open ? group.scheduledDate : null);
              }}
              canManage={canManage}
              submitting={submitting}
              onAddForDay={() => openCreate(group.scheduledDate)}
              onRescheduleDay={() => {
                const studioSession = group.sessions.find((s) => s.roomBookingId);
                setRescheduleDay({
                  scheduledDate: group.scheduledDate,
                  performerCount: group.sessions.length,
                  studioSession: studioSession
                    ? {
                        roomBookingId: studioSession.roomBookingId,
                        roomId: studioSession.roomId,
                        roomName: studioSession.roomName,
                        bookingStartTime: studioSession.bookingStartTime,
                        bookingEndTime: studioSession.bookingEndTime,
                      }
                    : null,
                });
              }}
              onEditSession={(session) =>
                setDialog({ open: true, mode: "edit", session })
              }
              onStatusChange={handleStatusChange}
              onBulkStatusChange={async (sessionIds, status) => {
                await bulkUpdateStatus.mutateAsync({ sessionIds, status });
              }}
            />
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-4 border-t px-5 py-2 text-muted-foreground text-xs">
        {(
          Object.values(REHEARSAL_SESSION_STATUS) as Array<
            (typeof REHEARSAL_SESSION_STATUS)["planned"]
          >
        ).map((s) => (
          <span key={s.id} className="flex items-center gap-1.5">
            <span
              className={`inline-block size-2 rounded-full ${s.dotClass}`}
            />
            {s.label}
          </span>
        ))}
      </div>

      {dialog.open && (
        <RehearsalSessionDialog
          key={
            dialog.mode === "edit"
              ? dialog.session.id
              : `create-${dialog.defaultDate ?? ""}`
          }
          open
          mode={dialog.mode}
          initial={dialog.mode === "edit" ? dialog.session : null}
          defaultDate={
            dialog.mode === "create" ? dialog.defaultDate : undefined
          }
          eventPerformers={eventPerformers}
          existingSessions={sessions}
          defaultRoomId={defaultRoomId}
          defaultRoomName={defaultRoomName}
          canBookStudio={canBookStudio}
          submitting={submitting}
          onOpenChange={(open) => {
            if (!open) setDialog({ open: false });
          }}
          onSubmitCreate={async (input: CreateRehearsalSessionsBulkPayload) => {
            await createBulk.mutateAsync(input);
            setDialog({ open: false });
          }}
          onSubmitEdit={async (input: UpdateRehearsalSessionDTO) => {
            if (dialog.mode !== "edit") return;
            await updateSession.mutateAsync({
              sessionId: dialog.session.id,
              input,
            });
            setDialog({ open: false });
          }}
          onDelete={
            dialog.mode === "edit"
              ? async () => {
                  await deleteSession.mutateAsync(dialog.session.id);
                  setDialog({ open: false });
                }
              : undefined
          }
        />
      )}

      {rescheduleDay ? (
        <RehearsalDayRescheduleDialog
          open
          fromScheduledDate={rescheduleDay.scheduledDate}
          performerCount={rescheduleDay.performerCount}
          studioSession={rescheduleDay.studioSession}
          canBookStudio={canBookStudio}
          submitting={submitting}
          onOpenChange={(open) => {
            if (!open) setRescheduleDay(null);
          }}
          onSubmit={async (input) => {
            await bulkReschedule.mutateAsync(input);
            setRescheduleDay(null);
          }}
        />
      ) : null}
    </div>
  );
}

type DateGroup = ReturnType<typeof groupSessionsByDate>[number];

function splitRehearsalDisplayDate(scheduledDate: string) {
  const formatted = formatDateLocal(scheduledDate);
  const [day = "", month = "", year = ""] = formatted.split("/");
  return { formatted, day, month, year };
}

type RehearsalDayBlockProps = {
  group: DateGroup;
  isToday?: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  canManage: boolean;
  submitting: boolean;
  onAddForDay: () => void;
  onRescheduleDay: () => void;
  onEditSession: (session: RehearsalSessionDTO) => void;
  onStatusChange: (
    session: RehearsalSessionDTO,
    status: RehearsalSessionDTO["status"],
  ) => Promise<void>;
  onBulkStatusChange: (
    sessionIds: string[],
    status: RehearsalSessionDTO["status"],
  ) => Promise<void>;
};

function RehearsalDayBlock({
  group,
  isToday = false,
  isOpen,
  onOpenChange,
  canManage,
  submitting,
  onAddForDay,
  onRescheduleDay,
  onEditSession,
  onStatusChange,
  onBulkStatusChange,
}: RehearsalDayBlockProps) {
  const statusCounts = summarizeRehearsalDayStatus(group.sessions);
  const isFullyCancelled =
    group.sessions.length > 0 &&
    statusCounts.cancelled === group.sessions.length;
  const { formatted, day, month, year } = splitRehearsalDisplayDate(
    group.scheduledDate,
  );
  const studioSession = group.sessions.find((s) => s.roomBookingId);
  const studioLabel =
    studioSession?.roomName && studioSession.bookingStartTime
      ? `${studioSession.roomName} · ${formatTime(studioSession.bookingStartTime)}–${formatTime(studioSession.bookingEndTime ?? "")}`
      : null;

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<RehearsalSessionDTO["status"]>(
    "completed",
  );

  const sessionIds = useMemo(
    () => group.sessions.map((s) => s.id),
    [group.sessions],
  );

  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) {
        if (sessionIds.includes(id)) next.add(id);
      }
      return next;
    });
  }, [sessionIds]);

  useEffect(() => {
    if (!isOpen) setSelectedIds(new Set());
  }, [isOpen]);

  const allSelected =
    sessionIds.length > 0 && sessionIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;
  const selectedCount = selectedIds.size;

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(sessionIds) : new Set());
  };

  const toggleOne = (sessionId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(sessionId);
      else next.delete(sessionId);
      return next;
    });
  };

  const handleBulkUpdate = async () => {
    if (selectedCount === 0) return;
    await onBulkStatusChange([...selectedIds], bulkStatus);
    setSelectedIds(new Set());
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={onOpenChange}
      className={`group/collapsible overflow-hidden rounded-lg border-2 border-border bg-background shadow-sm ${
        isFullyCancelled ? "opacity-55" : ""
      }`}
    >
      <CollapsibleTrigger asChild>
        <header
          className={`flex w-full cursor-pointer flex-wrap items-center justify-between gap-3 border-border border-l-4 px-4 py-3 text-left hover:bg-muted/60 group-data-[state=open]/collapsible:border-b ${
            isFullyCancelled
              ? "border-l-muted-foreground/40 bg-muted/20"
              : "border-l-primary bg-muted/40"
          }`}
        >
          <div className="flex min-w-0 items-center gap-3">
            <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
            <div
              className={`flex size-12 shrink-0 flex-col items-center justify-center rounded-md border bg-card px-1 font-semibold tabular-nums leading-none ${
                isFullyCancelled ? "text-muted-foreground line-through" : ""
              }`}
              aria-hidden
            >
              <span className="text-lg">{day}</span>
              <span className="text-[10px] text-muted-foreground">
                {month}/{year}
              </span>
            </div>
            <div className="min-w-0">
              <h4
                id={`rehearsal-day-${group.scheduledDate}`}
                className={`font-semibold text-base tracking-tight ${
                  isFullyCancelled
                    ? "text-muted-foreground line-through"
                    : ""
                }`}
              >
                ວັນຊ້ອມ {formatted}
                {isFullyCancelled ? (
                  <Badge
                    variant="outline"
                    className="ml-2 align-middle text-muted-foreground text-xs no-underline"
                  >
                    ຍົກເລີກທັງວັນ
                  </Badge>
                ) : isToday ? (
                  <Badge variant="secondary" className="ml-2 align-middle text-xs">
                    ມື້ນີ້
                  </Badge>
                ) : null}
              </h4>
              <p className="text-muted-foreground text-xs">
                {group.sessions.length} ຄົນ
                {statusCounts.completed > 0
                  ? ` · ສຳເລັດ ${statusCounts.completed}`
                  : ""}
                {statusCounts.planned > 0
                  ? ` · ວາງແຜນ ${statusCounts.planned}`
                  : ""}
                {statusCounts.cancelled > 0
                  ? ` · ຍົກເລີກ ${statusCounts.cancelled}`
                  : ""}
              </p>
            </div>
          </div>
          <div
            className="flex flex-wrap items-center gap-2"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {(
              Object.values(
                REHEARSAL_SESSION_STATUS,
              ) as RehearsalSessionStatusMeta[]
            )
              .filter((s) => statusCounts[s.id] > 0)
              .map((s) => (
                <span
                  key={s.id}
                  className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-0.5 text-xs"
                >
                  <span className={`size-1.5 rounded-full ${s.dotClass}`} />
                  {statusCounts[s.id]} {s.label.split(" (")[0]}
                </span>
              ))}
            {canManage && (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={submitting}
                  onClick={onRescheduleDay}
                >
                  <PencilIcon className="size-3.5" />
                  ແກ້ໄຂວັນ
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={submitting}
                  onClick={onAddForDay}
                >
                  <PlusIcon className="size-3.5" />
                  ເພີ່ມວັນນີ້
                </Button>
              </>
            )}
          </div>
        </header>
      </CollapsibleTrigger>

      <CollapsibleContent>
        {studioLabel && studioSession?.roomBookingId ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-primary/5 px-4 py-2 text-sm">
            <span className="flex items-center gap-2">
              <MapPinIcon className="size-4 text-primary" />
              <span>
                <span className="font-medium">ຈອງຫ້ອງ:</span> {studioLabel}
              </span>
            </span>
            <a
              href={`/app/room-bookings/${studioSession.roomBookingId}`}
              className="inline-flex items-center gap-1 text-primary text-xs underline"
            >
              ເບິ່ງການຈອງ
              <ExternalLinkIcon className="size-3" />
            </a>
          </div>
        ) : null}

        {canManage ? (
          <div className="flex flex-wrap items-center gap-3 border-b bg-muted/20 px-4 py-2.5">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={
                  allSelected
                    ? true
                    : someSelected
                      ? "indeterminate"
                      : false
                }
                onCheckedChange={(checked) => toggleAll(checked === true)}
                disabled={submitting || sessionIds.length === 0}
                aria-label="ເລືອກທັງໝົດ"
              />
              <span className="text-muted-foreground">
                {someSelected
                  ? `ເລືອກ ${selectedCount}/${sessionIds.length}`
                  : "ເລືອກທັງໝົດ"}
              </span>
            </label>

            {someSelected ? (
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Select
                  value={bulkStatus}
                  onValueChange={(value) =>
                    setBulkStatus(value as RehearsalSessionDTO["status"])
                  }
                  disabled={submitting}
                >
                  <SelectTrigger className="h-8 w-44">
                    <SelectValue placeholder="ເລືອກສະຖານະ" />
                  </SelectTrigger>
                  <SelectContent>
                    {(
                      Object.values(
                        REHEARSAL_SESSION_STATUS,
                      ) as RehearsalSessionStatusMeta[]
                    ).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  disabled={submitting}
                  onClick={() => void handleBulkUpdate()}
                >
                  ອັບເດດສະຖານະ ({selectedCount})
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        <ul className="divide-y divide-border">
          {group.sessions.map((session) => (
            <RehearsalSessionRow
              key={session.id}
              session={session}
              canManage={canManage}
              submitting={submitting}
              selected={selectedIds.has(session.id)}
              onSelectedChange={(checked) => toggleOne(session.id, checked)}
              onEdit={() => onEditSession(session)}
              onStatusChange={(status) => onStatusChange(session, status)}
            />
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

type RehearsalSessionRowProps = {
  session: RehearsalSessionDTO;
  canManage: boolean;
  submitting: boolean;
  selected: boolean;
  onSelectedChange: (checked: boolean) => void;
  onEdit: () => void;
  onStatusChange: (status: RehearsalSessionDTO["status"]) => void;
};

function RehearsalSessionRow({
  session,
  canManage,
  submitting,
  selected,
  onSelectedChange,
  onEdit,
  onStatusChange,
}: RehearsalSessionRowProps) {
  const meta = REHEARSAL_SESSION_STATUS[session.status];

  return (
    <li
      className={`grid gap-3 px-4 py-3 transition-colors hover:bg-muted/25 sm:items-center ${
        canManage
          ? "sm:grid-cols-[auto_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1fr)_auto]"
          : "sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1fr)_auto]"
      } ${selected ? "bg-primary/5" : ""}`}
    >
      {canManage ? (
        <div className="flex items-center">
          <Checkbox
            checked={selected}
            onCheckedChange={(checked) => onSelectedChange(checked === true)}
            disabled={submitting}
            aria-label={`ເລືອກ ${session.performerName ?? session.performerId}`}
          />
        </div>
      ) : null}

      <div className="min-w-0">
        <p className="font-medium text-sm">
          {session.performerName ?? session.performerId}
        </p>
        <p className="text-muted-foreground text-xs sm:hidden">ນັກສະແດງ</p>
      </div>

      <div>
        <p className="mb-1 text-muted-foreground text-xs sm:sr-only">ສະຖານະ</p>
        {canManage ? (
          <Select
            value={session.status}
            onValueChange={(value) =>
              onStatusChange(value as RehearsalSessionDTO["status"])
            }
            disabled={submitting}
          >
            <SelectTrigger className="h-8 w-full max-w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(
                Object.values(
                  REHEARSAL_SESSION_STATUS,
                ) as RehearsalSessionStatusMeta[]
              ).map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge variant={meta.badgeVariant}>{meta.label}</Badge>
        )}
      </div>

      <div className="min-w-0">
        <p className="mb-0.5 text-muted-foreground text-xs sm:sr-only">
          ສະຖານທີ່
        </p>
        <p className="truncate text-sm">
          {session.roomName
            ? session.bookingStartTime
              ? `${session.roomName} (${formatTime(session.bookingStartTime)}–${formatTime(session.bookingEndTime ?? "")})`
              : session.roomName
            : session.location || "—"}
        </p>
      </div>

      <div className="min-w-0">
        <p className="mb-0.5 text-muted-foreground text-xs sm:sr-only">
          ໝາຍເຫດ
        </p>
        <p className="truncate text-muted-foreground text-sm">
          {session.notes || "—"}
        </p>
      </div>

      {canManage && (
        <div className="flex justify-end sm:justify-end">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8"
            disabled={submitting}
            onClick={onEdit}
          >
            <PencilIcon className="size-3.5" />
            ແກ້ໄຂ
          </Button>
        </div>
      )}
    </li>
  );
}
